/**
 * Область запросов рядов «Голоса клиента» (#2394 §9 п. 7) — проекты,
 * категория, демо и фильтр темы раздела, посчитанные один раз для всех
 * вкладок — из тех же полей адреса и того же правила демо, что
 * `useVoiceRequestBase` раздела, но без его окна: окно ставит вкладка
 * (`VoiceSeriesWindowContext`). Ставится за шлюзом раздела: закрытый
 * или непроверенный экран рядов не просит. Тут же — «Показать этот месяц»
 * вида «12 месяцев»: оболочка знает адрес.
 *
 * Значение стабильно, пока область не изменилась: перерисовка раздела (лист
 * разговора, отказ фильтра) не перерисовывает каждого читателя рядов.
 */
import { useCallback, useMemo, type ReactNode } from 'react';
import { useLockedDemoAsked } from '@/contexts/LockedDemoContext';
import { useAuth } from '@/contexts/UnifiedAuthContext';
import { ALL_CATEGORIES } from '../stream-filter/useStreamWindow';
import { useStreamDemoFilter } from '../stream-filter/useStreamDemoFilter';
import { VoiceSeriesScopeContext, type VoiceSeriesScopeValue } from './useVoiceSeries';
import type { VoiceSearch } from './voice-search';

const NO_PROJECTS: string[] = [];

interface VoiceSeriesScopeProps {
  search: VoiceSearch;
  onSearchChange: (patch: Partial<VoiceSearch>) => void;
  children: ReactNode;
}

export function VoiceSeriesScope({
  search,
  onSearchChange,
  children,
}: VoiceSeriesScopeProps) {
  const { apiClient, orgId } = useAuth();
  const { includeDemo, ready: demoReady } = useStreamDemoFilter(search, 'voice-section');
  const lockedDemo = useLockedDemoAsked();
  const { category, voiceTheme } = search;
  // From the address: the same array until the projects change.
  const projectIds = search.projectIds ?? NO_PROJECTS;
  const callCategory = search.category ?? ALL_CATEGORIES;
  // A month of the year view becomes the page's period — the conversations of
  // that month then fill every row, the expanded one too.
  const showMonth = useCallback(
    (instant: string, patch?: Partial<VoiceSearch>) =>
      onSearchChange({
        ...patch,
        period: 'month',
        anchor: instant,
        from: undefined,
        to: undefined,
        day: undefined,
        // The card's demo rule goes with its window, as a period step drops it
        // (`useStreamWindow`): the month counts by the organization's rule.
        demo: undefined,
      }),
    [onSearchChange]
  );
  const value = useMemo<VoiceSeriesScopeValue>(
    () => ({
      base: {
        apiClient,
        orgId,
        includeDemo,
        lockedDemo,
        demoReady,
        category,
        voiceTheme,
        projectIds,
        callCategory,
      },
      showMonth,
    }),
    [
      apiClient,
      orgId,
      includeDemo,
      lockedDemo,
      demoReady,
      category,
      voiceTheme,
      projectIds,
      callCategory,
      showMonth,
    ]
  );
  return (
    <VoiceSeriesScopeContext.Provider value={value}>
      {children}
    </VoiceSeriesScopeContext.Provider>
  );
}
